import React, { useState } from 'react';
import { TextField, InputAdornment, Checkbox, FormControlLabel, IconButton, Chip, Box, Button } from '@mui/material';
import { Search, X } from 'lucide-react';
import { toastError } from '../utils';

export default function ReleaseSearchBar({ onSearch, onClear, resultCount }) {
    const [inputValue, setInputValue] = useState('');
    const [searchFields, setSearchFields] = useState({ title: true, email: false, artist: false });
    const [isSearchActive, setIsSearchActive] = useState(false);

    const triggerSearch = () => {
        if (inputValue.trim().length < 3) {
            toastError('Enter at least 3 characters');
            return;
        }
        const searchFor = Object.entries(searchFields)
            .filter(([, checked]) => checked)
            .map(([key]) => key)
            .join(',');
        setIsSearchActive(true);
        onSearch(inputValue.trim(), searchFor);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            triggerSearch();
        }
    };

    const handleFieldChange = (field) => (e) => {
        const newFields = { ...searchFields, [field]: e.target.checked };
        const anyChecked = Object.values(newFields).some(Boolean);
        if (!anyChecked) {
            newFields.title = true;
            toastError('At least one search field required');
        }
        setSearchFields(newFields);
    };

    const handleClear = () => {
        setInputValue('');
        setIsSearchActive(false);
        onClear();
    };

    const checkboxSx = {
        color: '#64748B',
        '&.Mui-checked': { color: '#6C63FF' },
        padding: '2px',
    };

    const labelSx = {
        '& .MuiFormControlLabel-label': {
            fontSize: '0.8rem',
            color: '#94A3B8',
        },
        marginRight: 1,
        marginLeft: 0,
    };

    return (
        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <TextField
                type="text"
                variant="outlined"
                size="small"
                value={inputValue}
                onChange={(e) => {
                    const val = e.target.value;
                    setInputValue(val);
                    if (!val && isSearchActive) {
                        setIsSearchActive(false);
                        onClear();
                    }
                }}
                onKeyDown={handleKeyDown}
                placeholder="Search releases..."
                sx={{ minWidth: 320, flex: 1, maxWidth: 500 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search size={18} color="#94A3B8" />
                        </InputAdornment>
                    ),
                }}
            />
            <IconButton
                onClick={triggerSearch}
                size="small"
                sx={{
                    color: '#6C63FF',
                    backgroundColor: 'rgba(108, 99, 255, 0.1)',
                    '&:hover': { backgroundColor: 'rgba(108, 99, 255, 0.2)' },
                    width: 34,
                    height: 34,
                }}
            >
                <Search size={16} />
            </IconButton>
            <FormControlLabel
                control={<Checkbox checked={searchFields.title} onChange={handleFieldChange('title')} size="small" sx={checkboxSx} />}
                label="Title"
                sx={labelSx}
            />
            <FormControlLabel
                control={<Checkbox checked={searchFields.email} onChange={handleFieldChange('email')} size="small" sx={checkboxSx} />}
                label="Email"
                sx={labelSx}
            />
            <FormControlLabel
                control={<Checkbox checked={searchFields.artist} onChange={handleFieldChange('artist')} size="small" sx={checkboxSx} />}
                label="Artist"
                sx={labelSx}
            />
            {isSearchActive && (
                <Button
                    size="small"
                    onClick={handleClear}
                    startIcon={<X size={14} />}
                    sx={{
                        color: '#EF4444',
                        textTransform: 'none',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        backgroundColor: 'rgba(239, 68, 68, 0.12)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        borderRadius: '6px',
                        px: 1.5,
                        '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.2)' },
                    }}
                >
                    Clear
                </Button>
            )}
            {isSearchActive && resultCount !== null && (
                <Chip
                    label={`${resultCount} result${resultCount !== 1 ? 's' : ''}`}
                    size="small"
                    sx={{
                        backgroundColor: 'rgba(108, 99, 255, 0.12)',
                        color: '#6C63FF',
                        fontWeight: 600,
                        fontSize: '0.78rem',
                        height: 26,
                        marginLeft: 'auto',
                    }}
                />
            )}
        </Box>
    );
}
